
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { PublicCourseService } from '@/services/public-course-service';
import { PublicCourseData } from '@/types/public-course-2';
import { CoursePageHeader } from '@/components/public-course/CoursePageHeader';
import { LoadingState } from '@/components/public-course/LoadingState';
import { ModulesSection } from '@/components/public-course/ModulesSection';
import { CourseDescription } from '@/components/public-course/CourseDescription';
import { InstructorProfile } from '@/components/public-course/InstructorProfile';
import { ReviewsSummary } from '@/components/public-course/ReviewsSummary';
import { FAQAccordion } from '@/components/public-course/FAQAccordion';
import { TermsSection } from '@/components/public-course/TermsSection';
import { RelatedCoursesSection } from '@/components/public-course/RelatedCoursesSection';
import { FooterStrip } from '@/components/public-course/FooterStrip';
import { ErrorDisplay } from '@/components/public-course/ErrorDisplay';
import { SEOMetadata } from '@/components/public-course/SEOMetadata';
import { useAuth } from '@/providers/AuthProvider';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Book, Clock, GraduationCap } from 'lucide-react';
import { CourseNotFound } from '@/components/course/CourseNotFound';

export function PublicCoursePage() {
  const { username, courseSlug } = useParams<{ username: string; courseSlug: string }>();
  const [courseData, setCourseData] = useState<PublicCourseData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  useEffect(() => {
    const fetchCourseData = async () => {
      if (!username || !courseSlug) {
        console.error("Username or course slug is missing");
        return;
      }
      
      setIsLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const data = await PublicCourseService.getCourseBySlug(username, courseSlug);
        if (data.notFound) {
          setNotFound(true);
          setIsLoading(false); // Important: Set loading to false for not found case
        } else {
          setCourseData(data);
          setIsLoading(false); // Important: Set loading to false for success case
        }
      } catch (error) {
        console.error("Failed to load course data", error);
        setError(error instanceof Error ? error : new Error("Failed to load course"));
        setIsLoading(false); // Important: Set loading to false for error case
      }
    };

    fetchCourseData();
  }, [username, courseSlug]);
  
  useEffect(() => {
    const checkEnrollment = async () => {
      if (!user || !courseData?.course?.id) return;
      
      try {
        const enrolled = await PublicCourseService.isUserEnrolled(user.id, courseData.course.id);
        setIsEnrolled(enrolled);
      } catch (error) {
        console.error("Failed to check enrollment status", error);
      }
    };
    
    checkEnrollment();
  }, [user, courseData]);

  const handleEnroll = async () => {
    if (!courseData?.course?.id || !username || !courseSlug) return;
    
    if (!user) {
      navigate('/student-auth', {
        state: { 
          from: location.pathname,
          courseId: courseData.course.id,
          coursePrice: courseData.course.price || 0,
          courseName: courseData.course.title,
          username,
          courseSlug
        }
      });
      return;
    }
    
    setIsEnrolling(true);
    try {
      // Check if already enrolled first
      const enrolled = await PublicCourseService.isUserEnrolled(user.id, courseData.course.id);
      
      if (enrolled) {
        // Already enrolled, redirect to course player
        navigate(`/dashboard/student/my-learning/course/${courseData.course.id}`);
        return;
      }
      
      if (courseData.course.price && courseData.course.price > 0) {
        // Always use the course-specific checkout route with username and courseSlug
        navigate(`/${username}/${courseSlug}/checkout`, {
          state: {
            courseId: courseData.course.id,
            courseName: courseData.course.title,
            coursePrice: courseData.course.price,
            from: location.pathname,
            username,
            courseSlug
          }
        });
      } else {
        await PublicCourseService.enrollUserInCourse(user.id, courseData.course.id);
        setIsEnrolled(true);
        toast.success("Successfully enrolled in course!");
        navigate(`/dashboard/student/my-learning/course/${courseData.course.id}`);
      }
    } catch (error) {
      console.error("Failed to enroll user", error);
      toast.error("Failed to enroll in course. Please try again.");
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Successfully logged out");
      // Navigation is now handled in the AuthProvider
    } catch (error) {
      console.error("Error during logout:", error);
      toast.error("Logout failed");
    }
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (notFound) {
    return <CourseNotFound />;
  }

  if (error) {
    return <ErrorDisplay error={error} onRetry={() => window.location.reload()} />;
  }
  
  if (!courseData) {
    return <CourseNotFound />;
  }

  const { course, modules, creator, additionalInfo } = courseData;
  const defaultTitle = `${course.title} | ${creator.full_name || creator.username} | Showee`;
  const seoTitle = additionalInfo?.seoTitle || defaultTitle;
  const seoDescription = additionalInfo?.seoDescription || course.description;
  
  const lessonCount = modules.reduce((count, module) => count + (module.lessons?.length || 0), 0);
  const totalDuration = modules.reduce((total, module) => {
    const moduleDuration = module.lessons?.reduce((sum, lesson) => sum + (lesson.time_estimate_minutes || 15), 0) || 30;
    return total + moduleDuration;
  }, 0);

  return (
    <div className="min-h-screen bg-background">
      <SEOMetadata 
        title={seoTitle}
        description={seoDescription}
        imageUrl={course.thumbnail_url}
        creator={creator.full_name || creator.username || ''}
        defaultTitle={defaultTitle}
      />

      <div className="relative w-full h-[300px] md:h-[400px] overflow-hidden bg-gradient-to-b from-muted/30 to-background">
        {course.thumbnail_url ? (
          <>
            <img
              src={course.thumbnail_url}
              alt={course.title}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/60 to-background"></div>
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-background"></div>
        )}
      </div>

      <CoursePageHeader 
        course={course}
        creator={creator}
        moduleCount={modules.length}
        lessonCount={lessonCount}
        totalDuration={totalDuration}
        additionalInfo={additionalInfo}
        onLogout={handleLogout}
        user={user}
      />

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-16">
            <CourseDescription course={course} additionalInfo={additionalInfo} />
            <ModulesSection modules={modules} />
            <InstructorProfile creator={creator} additionalInfo={additionalInfo} />
            <ReviewsSummary courseId={course.id} showFeaturedReviews={course.show_featured_reviews} />
            {additionalInfo?.faq && additionalInfo.faq.length > 0 && (
              <FAQAccordion faqs={additionalInfo.faq} />
            )}
            <TermsSection 
              terms={additionalInfo?.terms}
              refundPolicy={additionalInfo?.refundPolicy}
              requirements={additionalInfo?.requirements}
            />
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <Card className="overflow-hidden">
                {course.thumbnail_url && (
                  <div className="aspect-video w-full overflow-hidden">
                    <img 
                      src={course.thumbnail_url} 
                      alt={course.title} 
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="space-y-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold">
                        {course.price ? `$${course.price}` : 'Free'}
                      </p>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="h-6 w-6 rounded-full bg-primary/10 grid place-items-center text-primary">
                          <Book className="h-4 w-4" />
                        </div>
                        <span>{modules.length} modules</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="h-6 w-6 rounded-full bg-primary/10 grid place-items-center text-primary">
                          <GraduationCap className="h-4 w-4" />
                        </div>
                        <span>{lessonCount} lessons</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="h-6 w-6 rounded-full bg-primary/10 grid place-items-center text-primary">
                          <Clock className="h-4 w-4" />
                        </div>
                        <span>{formatDuration(totalDuration)} total length</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="px-6 pb-6 pt-0">
                  <Button
                    onClick={handleEnroll}
                    disabled={isEnrolling || isEnrolled}
                    className="w-full"
                    size="lg"
                    aria-label={isEnrolling 
                      ? 'Processing enrollment' 
                      : isEnrolled 
                      ? 'Go to enrolled course' 
                      : `Enroll in ${course.title}`}
                  >
                    {isEnrolling 
                      ? 'Processing...' 
                      : isEnrolled 
                      ? 'Go to Course' 
                      : course.price > 0 
                      ? `Enroll Now` 
                      : 'Enroll for Free'}
                  </Button>
                </CardFooter>
              </Card>
              
              {creator && creator.id && (
                <div className="mt-8">
                  <RelatedCoursesSection 
                    creatorId={creator.id} 
                    currentCourseId={course.id}
                    creatorUsername={creator.username || undefined}
                  />
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
      
      <FooterStrip showBranding={!additionalInfo?.hideBranding} />
    </div>
  );
}
